import React, { useState, useEffect, useRef } from 'react';
import { 
  PlayCircle, PauseCircle, RotateCcw, Palette, Circle, 
  Timer, Heart, Waves, Sparkles, Zap, Moon, Sun, Grid3X3 
} from 'lucide-react';

const RelaxGames = () => {
  const [activeGame, setActiveGame] = useState('bubbles');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedColors, setSelectedColors] = useState(['#10b981']);
  const [currentColorIndex, setCurrentColorIndex] = useState(0);
  
  // Bubble Game State
  const [bubbles, setBubbles] = useState([]);
  const [poppedCount, setPoppedCount] = useState(0);
  
  // Stress Ball State
  const [ballPressed, setBallPressed] = useState(false);
  const [stressLevel, setStressLevel] = useState(100);
  const [showFireworks, setShowFireworks] = useState(false);
  const [fireworks, setFireworks] = useState([]);
  const [previousStressLevel, setPreviousStressLevel] = useState(100);

  // Memory/Matching Pairs Game State
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameWon, setGameWon] = useState(false);

  // Snake Game State
  const [snakePosition, setSnakePosition] = useState([[10, 10]]);
  const [food, setFood] = useState([15, 15]);
  const [direction, setDirection] = useState('RIGHT');
  const [nextDirection, setNextDirection] = useState('RIGHT');
  const [snakeGameOver, setSnakeGameOver] = useState(false);
  const [snakeScore, setSnakeScore] = useState(0);
  const [snakeHighScore, setSnakeHighScore] = useState(
    parseInt(localStorage.getItem('snakeHighScore')) || 0
  );
  const [snakePaused, setSnakePaused] = useState(false);
  const [snakeDifficulty, setSnakeDifficulty] = useState('medium');
  const [foodEatenFlash, setFoodEatenFlash] = useState(false);
  const gameLoopRef = useRef(null);
  const directionRef = useRef('RIGHT');
  const nextDirectionRef = useRef('RIGHT');
  const foodRef = useRef([15, 15]);
  const scoreRef = useRef(0);

  // Removed breathing and meditation logic - moved to wellness page

  // Bubble Generation
  useEffect(() => {
    if (activeGame === 'bubbles') {
      const interval = setInterval(() => {
        setBubbles(prev => {
          const newBubbles = [...prev];
          if (newBubbles.length < 10) {
            newBubbles.push({
              id: Date.now() + Math.random(),
              x: Math.random() * 80 + 10, // 10-90% of screen
              y: 100,
              size: Math.random() * 50 + 30,
              speed: Math.random() * 2 + 1,
              opacity: 1
            });
          }
          
          // Move bubbles up and remove old ones
          return newBubbles
            .map(bubble => ({
              ...bubble,
              y: bubble.y - bubble.speed,
              opacity: bubble.opacity - 0.01
            }))
            .filter(bubble => bubble.y > -100 && bubble.opacity > 0);
        });
      }, 2000);
      
      return () => clearInterval(interval);
    }
  }, [activeGame]);

  // Drawing Canvas Logic
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && activeGame === 'drawing') {
      // Use setTimeout to ensure canvas element has proper dimensions
      setTimeout(() => {
        const rect = canvas.getBoundingClientRect();
        // Set canvas dimensions with device pixel ratio for better quality
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        
        const ctx = canvas.getContext('2d');
        
        // Scale the context to account for device pixel ratio
        ctx.scale(dpr, dpr);
        
        // Set canvas CSS dimensions back to original
        canvas.style.width = rect.width + 'px';
        canvas.style.height = rect.height + 'px';
        
        // Only set background and drawing properties on initial load, not when colors change
        if (activeGame === 'drawing') {
          // Set initial canvas background
          ctx.fillStyle = '#f0fdfa';
          ctx.fillRect(0, 0, rect.width, rect.height);
        }
        
        // Set drawing properties
        ctx.strokeStyle = selectedColors[0];
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }, 100);
    }
  }, [activeGame]); // Removed selectedColors dependency to prevent clearing canvas

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Get the actual pointer position accounting for touch events
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    if (activeGame !== 'drawing') return;
    
    e.preventDefault();
    setIsDrawing(true);
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const pos = getMousePos(e);
    
    // Get current color from selected colors array
    const currentColor = selectedColors[currentColorIndex % selectedColors.length];
    
    // Ensure drawing properties are set correctly
    ctx.strokeStyle = currentColor;
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = 'source-over';
    
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing || activeGame !== 'drawing') return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const pos = getMousePos(e);
    
    // Use the selected color from dropdown
    ctx.strokeStyle = selectedColors[0];
    
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    
    e.preventDefault();
    setIsDrawing(false);
    
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      
      // Cycle to next color for the next drawing stroke
      setCurrentColorIndex(prev => (prev + 1) % selectedColors.length);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      
      // Clear the entire canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Reset background
      ctx.fillStyle = '#f0fdfa';
      ctx.fillRect(0, 0, rect.width, rect.height);
      
      // Reset drawing properties
      ctx.strokeStyle = selectedColors[0];
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  };

  const popBubble = (bubbleId) => {
    setBubbles(prev => prev.filter(bubble => bubble.id !== bubbleId));
    setPoppedCount(prev => prev + 1);
  };

  const handleStressBall = (pressed) => {
    setBallPressed(pressed);
    if (pressed && stressLevel > 0) {
      const newStressLevel = Math.max(0, stressLevel - 5);
      
      // Trigger fireworks if stress level decreased
      if (newStressLevel < previousStressLevel) {
        triggerFireworks();
      }
      
      setPreviousStressLevel(stressLevel);
      setStressLevel(newStressLevel);
    }
  };

  const triggerFireworks = () => {
    setShowFireworks(true);
    
    // Create multiple firework particles
    const newFireworks = Array.from({ length: 15 }, (_, i) => ({
      id: Date.now() + i,
      x: 50 + (Math.random() - 0.5) * 40, // Spread around center
      y: 50 + (Math.random() - 0.5) * 40,
      angle: (Math.PI * 2 * i) / 15,
      color: ['#fbbf24', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#3b82f6'][Math.floor(Math.random() * 6)]
    }));
    
    setFireworks(newFireworks);
    
    // Clear fireworks after animation
    setTimeout(() => {
      setShowFireworks(false);
      setFireworks([]);
    }, 1000);
  };

  // formatTime function removed - moved to wellness page with meditation timer

  // Initialize Memory Game
  const initializeMemoryGame = () => {
    const symbols = ['❤️', '🌸', '🌟', '🦋', '🌈', '🌺', '✨', '🍀'];
    const pairs = [...symbols, ...symbols];
    const shuffled = pairs.sort(() => Math.random() - 0.5).map((symbol, index) => ({
      id: index,
      symbol,
      isFlipped: false,
      isMatched: false
    }));
    setCards(shuffled);
    setFlippedCards([]);
    setMatchedPairs([]);
    setMoves(0);
    setGameWon(false);
  };

  const handleCardClick = (cardId) => {
    if (flippedCards.length === 2 || flippedCards.includes(cardId)) return;
    
    const card = cards.find(c => c.id === cardId);
    if (card.isMatched) return;

    const newFlipped = [...flippedCards, cardId];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(moves + 1);
      const [first, second] = newFlipped;
      const firstCard = cards.find(c => c.id === first);
      const secondCard = cards.find(c => c.id === second);

      if (firstCard.symbol === secondCard.symbol) {
        setMatchedPairs([...matchedPairs, firstCard.symbol]);
        setCards(cards.map(c => 
          c.id === first || c.id === second ? { ...c, isMatched: true } : c
        ));
        setFlippedCards([]);
        
        if (matchedPairs.length + 1 === 8) {
          setGameWon(true);
        }
      } else {
        setTimeout(() => setFlippedCards([]), 1000);
      }
    }
  };

  useEffect(() => {
    if (activeGame === 'memory') {
      initializeMemoryGame();
    }
  }, [activeGame]);

  // Snake Game Functions
  const GRID_SIZE = 20;
  const CELL_SIZE = 20;

  const getSpeed = () => {
    switch (snakeDifficulty) {
      case 'easy': return 400;
      case 'medium': return 280;
      case 'hard': return 180;
      default: return 280;
    }
  };

  const initializeSnakeGame = () => {
    // Start with just 1 segment (the head)
    const initialSnake = [[10, 10]];
    const initialFood = generateFood(initialSnake);
    setSnakePosition(initialSnake);
    setFood(initialFood);
    setDirection('RIGHT');
    setNextDirection('RIGHT');
    directionRef.current = 'RIGHT';
    nextDirectionRef.current = 'RIGHT';
    foodRef.current = initialFood;
    scoreRef.current = 0;
    setSnakeGameOver(false);
    setSnakeScore(0);
    setSnakePaused(false);
  };

  const generateFood = (snake) => {
    let newFood;
    do {
      newFood = [
        Math.floor(Math.random() * GRID_SIZE),
        Math.floor(Math.random() * GRID_SIZE)
      ];
    } while (snake.some(segment => segment[0] === newFood[0] && segment[1] === newFood[1]));
    return newFood;
  };

  const moveSnake = () => {
    if (snakeGameOver || snakePaused) return;
    
    directionRef.current = nextDirectionRef.current;
    setDirection(nextDirectionRef.current);
    
    // Get current food position from ref
    const currentFood = foodRef.current;
    
    setSnakePosition(prevSnake => {
      console.log('=== MOVE START === Snake length:', prevSnake.length, 'Food at:', currentFood);
      const head = prevSnake[0];
      let newX = head[0];
      let newY = head[1];

      // Calculate new position based on direction
      switch (nextDirectionRef.current) {
        case 'UP':
          newY = head[1] - 1;
          break;
        case 'DOWN':
          newY = head[1] + 1;
          break;
        case 'LEFT':
          newX = head[0] - 1;
          break;
        case 'RIGHT':
          newX = head[0] + 1;
          break;
        default:
          break;
      }

      // Wrap around edges - modulo operation for seamless wrapping
      newX = (newX + GRID_SIZE) % GRID_SIZE;
      newY = (newY + GRID_SIZE) % GRID_SIZE;

      const newHead = [newX, newY];
      
      console.log('New head position:', newHead, 'Food position:', currentFood);

      // Check self collision
      if (prevSnake.some(segment => segment[0] === newHead[0] && segment[1] === newHead[1])) {
        console.log('💀 Self collision!');
        setSnakeGameOver(true);
        return prevSnake;
      }

      // Check food collision BEFORE adding to snake
      const foodEaten = newHead[0] === currentFood[0] && newHead[1] === currentFood[1];
      
      console.log('Collision check:', foodEaten ? '✅ FOOD EATEN!' : '❌ No collision');
      
      if (foodEaten) {
        console.log('🍎🍎🍎 FOOD EATEN! GROWING SNAKE! 🍎🍎🍎');
        console.log('  - Old length:', prevSnake.length);
        console.log('  - New length will be:', prevSnake.length + 1);
        
        // Visual feedback
        setFoodEatenFlash(true);
        setTimeout(() => setFoodEatenFlash(false), 200);
        
        // Update score
        const newScore = scoreRef.current + 10;
        scoreRef.current = newScore;
        setSnakeScore(newScore);
        if (newScore > snakeHighScore) {
          setSnakeHighScore(newScore);
          localStorage.setItem('snakeHighScore', newScore.toString());
        }
        
        // Generate new food and update ref
        const newFood = generateFood([newHead, ...prevSnake]);
        foodRef.current = newFood;
        setFood(newFood);
        console.log('  - New food generated at:', newFood);
        
        // Return snake WITH new head AND old tail (growth!)
        const grownSnake = [newHead, ...prevSnake];
        console.log('✅ GREW! New snake length:', grownSnake.length);
        return grownSnake;
      }

      // Normal move: add new head, remove tail (no growth)
      const movedSnake = [newHead, ...prevSnake.slice(0, -1)];
      return movedSnake;
    });
  };

  useEffect(() => {
    if (activeGame === 'snake' && !snakeGameOver && !snakePaused) {
      const interval = setInterval(moveSnake, getSpeed());
      gameLoopRef.current = interval;
      return () => clearInterval(interval);
    } else if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
      gameLoopRef.current = null;
    }
  }, [activeGame, snakeGameOver, snakePaused, snakeDifficulty]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (activeGame !== 'snake') return;
      
      // Allow 'R' key to restart anytime
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        initializeSnakeGame();
        return;
      }
      
      if (snakeGameOver) return;
      
      e.preventDefault();
      
      switch (e.key) {
        case 'ArrowUp':
          if (directionRef.current !== 'DOWN') {
            setNextDirection('UP');
            nextDirectionRef.current = 'UP';
          }
          break;
        case 'ArrowDown':
          if (directionRef.current !== 'UP') {
            setNextDirection('DOWN');
            nextDirectionRef.current = 'DOWN';
          }
          break;
        case 'ArrowLeft':
          if (directionRef.current !== 'RIGHT') {
            setNextDirection('LEFT');
            nextDirectionRef.current = 'LEFT';
          }
          break;
        case 'ArrowRight':
          if (directionRef.current !== 'LEFT') {
            setNextDirection('RIGHT');
            nextDirectionRef.current = 'RIGHT';
          }
          break;
        case ' ':
          setSnakePaused(prev => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [activeGame, snakeGameOver]);

  useEffect(() => {
    if (activeGame === 'snake') {
      initializeSnakeGame();
    }
  }, [activeGame]);

  // Debug: Log food position changes
  useEffect(() => {
    console.log('🍊 Food state changed to:', food, '| foodRef:', foodRef.current);
  }, [food]);

  const games = [
    { id: 'bubbles', name: 'Bubble Pop', icon: <Circle className="w-5 h-5" />, desc: 'Pop floating bubbles' },
    { id: 'memory', name: 'Matching Pairs', icon: <Heart className="w-5 h-5" />, desc: 'Memory card game' },
    { id: 'drawing', name: 'Mindful Drawing', icon: <Palette className="w-5 h-5" />, desc: 'Free-form art therapy' },
    { id: 'snake', name: 'Snake Game', icon: <Grid3X3 className="w-5 h-5" />, desc: 'Classic snake game' },
    { id: 'stressball', name: 'Stress Ball', icon: <Zap className="w-5 h-5" />, desc: 'Digital stress relief' },
  ];

  const renderGame = () => {
    switch (activeGame) {
      case 'snake':
        return (
          <div className="space-y-6">
            {/* Header with Score and Controls */}
            <div className="flex items-start justify-between flex-wrap gap-4">
              {/* Left: Score Display */}
              <div className="flex items-center space-x-2 md:space-x-4">
                <div className="bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl p-2 md:p-4 shadow-lg border-2 border-emerald-200">
                  <p className="text-xs text-emerald-600 font-medium mb-1">Score</p>
                  <p className="text-xl md:text-2xl font-bold text-emerald-700">{snakeScore}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl p-2 md:p-4 shadow-lg border-2 border-blue-200">
                  <p className="text-xs text-blue-600 font-medium mb-1">Length</p>
                  <p className="text-xl md:text-2xl font-bold text-blue-700">{snakePosition.length}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl p-2 md:p-4 shadow-lg border-2 border-amber-200">
                  <p className="text-xs text-amber-600 font-medium mb-1">High Score</p>
                  <p className="text-xl md:text-2xl font-bold text-amber-700">{snakeHighScore}</p>
                </div>
              </div>
              
              {/* Right: Controls in Column */}
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  <select
                    value={snakeDifficulty}
                    onChange={(e) => {
                      setSnakeDifficulty(e.target.value);
                      initializeSnakeGame();
                    }}
                    className="px-3 py-2 border-2 border-emerald-200 rounded-lg bg-white text-emerald-800 font-medium focus:border-emerald-500 focus:outline-none h-10"
                    disabled={!snakeGameOver && snakeScore > 0}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                  {!snakeGameOver && (
                    <button
                      onClick={() => setSnakePaused(!snakePaused)}
                      className="btn-secondary flex items-center space-x-2 h-10"
                    >
                      {snakePaused ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
                      <span>{snakePaused ? 'Resume' : 'Pause'}</span>
                    </button>
                  )}
                </div>
                <button
                  onClick={initializeSnakeGame}
                  className="btn-primary flex items-center justify-center space-x-2 h-10 w-full"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>{snakeGameOver ? 'New Game' : 'Restart'}</span>
                </button>
              </div>
            </div>

            {/* Game Board */}
            <div className="relative flex justify-center">
              <div 
                className={`bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl shadow-xl border-4 overflow-hidden w-full max-w-md transition-all ${foodEatenFlash ? 'border-amber-500 shadow-2xl' : 'border-emerald-300'}`}
                style={{ 
                  aspectRatio: '1/1',
                  position: 'relative',
                  maxWidth: '400px'
                }}
              >
                {/* Grid lines for visual effect */}
                <div className="absolute inset-0 pointer-events-none" style={{
                  backgroundImage: 'linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px)',
                  backgroundSize: `${100 / GRID_SIZE}% ${100 / GRID_SIZE}%`
                }}></div>

                {/* Snake */}
                {snakePosition.map((segment, index) => (
                  <div
                    key={`snake-${index}-${segment[0]}-${segment[1]}`}
                    className="absolute"
                    style={{
                      left: `${(segment[0] / GRID_SIZE) * 100}%`,
                      top: `${(segment[1] / GRID_SIZE) * 100}%`,
                      width: `${(1 / GRID_SIZE) * 100 - 0.5}%`,
                      height: `${(1 / GRID_SIZE) * 100 - 0.5}%`,
                      borderRadius: index === 0 ? '20%' : '15%',
                      background: index === 0 
                        ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' 
                        : `linear-gradient(135deg, #10b981 0%, #34d399 100%)`,
                      boxShadow: index === 0 
                        ? '0 0.3vw 1vw rgba(5, 150, 105, 0.6)' 
                        : '0 0.1vw 0.3vw rgba(16, 185, 129, 0.3)',
                      zIndex: snakePosition.length - index,
                      border: index === 0 ? '0.2vw solid rgba(255, 255, 255, 0.3)' : 'none'
                    }}
                  >
                    {index === 0 && (
                      <div className="w-full h-full flex items-center justify-center text-white font-bold" style={{ fontSize: 'clamp(8px, 1.5vw, 12px)' }}>
                        {direction === 'UP' && '▲'}
                        {direction === 'DOWN' && '▼'}
                        {direction === 'LEFT' && '◀'}
                        {direction === 'RIGHT' && '▶'}
                      </div>
                    )}
                  </div>
                ))}

                {/* Food */}
                <div
                  key={`food-${food[0]}-${food[1]}`}
                  className="absolute rounded-full animate-pulse"
                  style={{
                    left: `${(food[0] / GRID_SIZE) * 100}%`,
                    top: `${(food[1] / GRID_SIZE) * 100}%`,
                    width: `${(1 / GRID_SIZE) * 100 - 0.5}%`,
                    height: `${(1 / GRID_SIZE) * 100 - 0.5}%`,
                    background: 'radial-gradient(circle at 30% 30%, #fbbf24 0%, #f59e0b 50%, #ef4444 100%)',
                    boxShadow: '0 0.3vw 1.5vw rgba(245, 158, 11, 0.7)',
                    zIndex: 1,
                    border: '0.2vw solid rgba(255, 255, 255, 0.4)'
                  }}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="rounded-full opacity-70 bg-white" style={{ width: '25%', height: '25%' }}></div>
                  </div>
                </div>

                {/* Game Over Overlay */}
                {snakeGameOver && (
                  <div className="absolute inset-0 bg-gradient-to-br from-black/70 to-emerald-900/70 backdrop-blur-md flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl p-10 text-center space-y-6 animate-fade-in-up shadow-2xl border-4 border-emerald-200 max-w-sm">
                      <div className="w-20 h-20 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-4xl">😔</span>
                      </div>
                      <h3 className="text-4xl font-bold text-emerald-800">Game Over!</h3>
                      <div className="bg-emerald-50 rounded-xl p-4 border-2 border-emerald-200">
                        <p className="text-sm text-emerald-600 font-medium mb-1">Final Score</p>
                        <p className="text-4xl font-bold text-emerald-700">{snakeScore}</p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                        <p className="text-sm text-blue-600 font-medium mb-1">Final Length</p>
                        <p className="text-2xl font-bold text-blue-700">{snakePosition.length} segments</p>
                      </div>
                      {snakeScore === snakeHighScore && snakeScore > 0 && (
                        <div className="bg-gradient-to-r from-amber-100 to-yellow-100 rounded-xl p-4 border-2 border-amber-300 animate-pulse">
                          <p className="text-2xl font-bold text-amber-700">🎉 New High Score! 🎉</p>
                        </div>
                      )}
                      <button
                        onClick={initializeSnakeGame}
                        className="btn-primary flex items-center space-x-2 mx-auto text-lg px-8 py-4 w-full"
                      >
                        <RotateCcw className="w-5 h-5" />
                        <span>Restart Game</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Pause Overlay */}
                {snakePaused && !snakeGameOver && (
                  <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-emerald-900/50 backdrop-blur-md flex items-center justify-center z-40">
                    <div className="bg-white rounded-3xl p-8 text-center shadow-2xl border-4 border-emerald-200 animate-fade-in-up">
                      <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <PauseCircle className="w-12 h-12 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-emerald-800 mb-2">Game Paused</h3>
                      <p className="text-emerald-600 text-lg">Press Space or click Resume to continue</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Controls */}
            <div className="flex flex-col items-center space-y-2 md:space-y-3 mt-4 lg:hidden">
              <button
                onClick={() => {
                  if (directionRef.current !== 'DOWN') {
                    setNextDirection('UP');
                    nextDirectionRef.current = 'UP';
                  }
                }}
                className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 active:scale-95 text-white rounded-xl shadow-lg flex items-center justify-center text-2xl md:text-3xl font-bold transition-all"
                disabled={snakeGameOver}
              >
                ▲
              </button>
              <div className="flex space-x-2 md:space-x-3">
                <button
                  onClick={() => {
                    if (directionRef.current !== 'RIGHT') {
                      setNextDirection('LEFT');
                      nextDirectionRef.current = 'LEFT';
                    }
                  }}
                  className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 active:scale-95 text-white rounded-xl shadow-lg flex items-center justify-center text-2xl md:text-3xl font-bold transition-all"
                  disabled={snakeGameOver}
                >
                  ◀
                </button>
                <button
                  onClick={() => {
                    if (directionRef.current !== 'LEFT') {
                      setNextDirection('RIGHT');
                      nextDirectionRef.current = 'RIGHT';
                    }
                  }}
                  className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 active:scale-95 text-white rounded-xl shadow-lg flex items-center justify-center text-2xl md:text-3xl font-bold transition-all"
                  disabled={snakeGameOver}
                >
                  ▶
                </button>
              </div>
              <button
                onClick={() => {
                  if (directionRef.current !== 'UP') {
                    setNextDirection('DOWN');
                    nextDirectionRef.current = 'DOWN';
                  }
                }}
                className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 active:scale-95 text-white rounded-xl shadow-lg flex items-center justify-center text-2xl md:text-3xl font-bold transition-all"
                disabled={snakeGameOver}
              >
                ▼
              </button>
            </div>

            {/* Instructions */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-5 border-2 border-emerald-200 shadow-md">
              <h4 className="font-bold text-emerald-800 mb-3 text-lg flex items-center">
                <Sparkles className="w-5 h-5 mr-2" />
                How to Play
              </h4>
              <ul className="text-sm text-emerald-700 space-y-2">
                <li className="flex items-start">
                  <span className="text-emerald-500 mr-2">▸</span>
                  <span>Game starts automatically - use <strong>Arrow Keys</strong> to control direction</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-500 mr-2">▸</span>
                  <span>Eat the glowing orange food to <strong>grow longer</strong> and score +10 points</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-500 mr-2">▸</span>
                  <span>Snake wraps around edges - no walls to worry about!</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-500 mr-2">▸</span>
                  <span>Avoid running into your own body</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-500 mr-2">▸</span>
                  <span>Press <strong>Space</strong> to pause, <strong>R</strong> to restart anytime</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-500 mr-2">▸</span>
                  <span><strong>Difficulty:</strong> Easy (slower), Medium (balanced), Hard (faster)</span>
                </li>
              </ul>
            </div>
          </div>
        );

      case 'memory':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-md">
                  <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Moves: {moves}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-md">
                  <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Pairs: {matchedPairs.length}/8</p>
                </div>
              </div>
              <button
                onClick={initializeMemoryGame}
                className="btn-primary flex items-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>New Game</span>
              </button>
            </div>

            {gameWon && (
              <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl p-6 text-center animate-fade-in-up">
                <Sparkles className="w-12 h-12 mx-auto mb-3" />
                <h3 className="text-2xl font-bold mb-2">Congratulations! 🎉</h3>
                <p className="text-lg">You found all pairs in {moves} moves!</p>
              </div>
            )}

            <div className="grid grid-cols-4 gap-4">
              {cards.map((card) => (
                <button
                  key={card.id}
                  onClick={() => handleCardClick(card.id)}
                  disabled={card.isMatched}
                  className={`aspect-square rounded-xl text-4xl font-bold transition-all duration-300 transform hover:scale-105 ${
                    flippedCards.includes(card.id) || card.isMatched
                      ? 'bg-gradient-to-br from-emerald-400 to-green-500 text-white shadow-lg'
                      : 'bg-gradient-to-br from-emerald-100 to-green-100 dark:from-slate-700 dark:to-slate-600 shadow-md hover:shadow-xl'
                  } ${card.isMatched ? 'opacity-50' : ''}`}
                >
                  {flippedCards.includes(card.id) || card.isMatched ? card.symbol : '?'}
                </button>
              ))}
            </div>

            <div className="text-center text-emerald-600 dark:text-emerald-300">
              <p>Find matching pairs by clicking on the cards. Take your time and relax! 🌸</p>
            </div>
          </div>
        );

      case 'bubbles':
        return (
          <div className="relative h-96 bg-gradient-to-b from-blue-100 to-teal-100 rounded-xl overflow-hidden">
            <div className="absolute top-4 left-4 bg-white/80 rounded-lg p-2">
              <p className="text-sm font-medium text-emerald-700">Bubbles Popped: {poppedCount}</p>
            </div>
            
            {bubbles.map((bubble) => (
              <div
                key={bubble.id}
                className="absolute rounded-full bg-gradient-to-br from-blue-200 to-teal-200 border-2 border-white/50 cursor-pointer hover:scale-110 transition-transform"
                style={{
                  left: `${bubble.x}%`,
                  bottom: `${bubble.y}px`,
                  width: `${bubble.size}px`,
                  height: `${bubble.size}px`,
                  opacity: bubble.opacity,
                }}
                onClick={() => popBubble(bubble.id)}
              >
                <div className="w-full h-full rounded-full bg-gradient-to-br from-white/30 to-transparent"></div>
              </div>
            ))}
            
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <p className="text-emerald-700 text-center">Click the bubbles to pop them and relieve stress!</p>
            </div>
          </div>
        );

      case 'drawing':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-4">
                <select
                  value={selectedColors[0]}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    setSelectedColors([newColor]);
                    setCurrentColorIndex(0);
                  }}
                  className="px-3 py-1 border-2 border-emerald-200 rounded-lg bg-white text-emerald-800 font-medium focus:border-emerald-500 focus:outline-none"
                >
                  <option value="#10b981">🟢 Emerald Green</option>
                  <option value="#3b82f6">🔵 Blue</option>
                  <option value="#8b5cf6">🟣 Purple</option>
                  <option value="#f59e0b">🟡 Amber</option>
                  <option value="#ef4444">🔴 Red</option>
                  <option value="#6b7280">⚫ Gray</option>
                  <option value="#f97316">🟠 Orange</option>
                  <option value="#84cc16">🟢 Lime Green</option>
                  <option value="#06b6d4">🔵 Cyan</option>
                  <option value="#ec4899">🔴 Pink</option>
                  <option value="#8b5a3c">🟤 Brown</option>
                  <option value="#000000">⚫ Black</option>
                  <option value="#ffffff">⚪ White</option>
                  <option value="#fbbf24">🟡 Yellow</option>
                  <option value="#a78bfa">🟣 Light Purple</option>
                  <option value="#34d399">🟢 Light Green</option>
                </select>
                
                <div 
                  className="w-8 h-8 rounded-full border-2 border-gray-300"
                  style={{ backgroundColor: selectedColors[0] }}
                ></div>
              </div>
              <button
                onClick={clearCanvas}
                className="btn-secondary flex items-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Clear Canvas</span>
              </button>
            </div>
            
            <canvas
              ref={canvasRef}
              className="w-full h-48 border-2 border-emerald-200 rounded-xl cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            
            <p className="text-center text-emerald-600">Draw freely to express your thoughts and feelings</p>
          </div>
        );

      case 'stressball':
        // Determine emoji expression based on stress level (using mood tracker style emojis)
        const getStressBallEmoji = () => {
          if (stressLevel >= 67) {
            // High stress - Neutral/worried face
            return "😐";
          } else if (stressLevel >= 34) {
            // Medium stress - Slight smile
            return "🙂";
          } else if (stressLevel >= 11) {
            // Low stress - Happy face
            return "😊";
          } else {
            // Very low stress - Very happy
            return "😄";
          }
        };

        const stressBallEmoji = getStressBallEmoji();

        return (
          <div className="text-center space-y-8">
            <div className="relative flex items-center justify-center min-h-[300px]">
              {/* Fireworks effect */}
              {showFireworks && fireworks.map((fw) => (
                <div
                  key={fw.id}
                  className="absolute animate-ping"
                  style={{
                    left: `${fw.x}%`,
                    top: `${fw.y}%`,
                    animationDuration: '1s'
                  }}
                >
                  <div 
                    className="text-4xl"
                    style={{
                      color: fw.color,
                      transform: `rotate(${fw.angle}rad) translateY(-30px)`,
                      animation: 'sparkle 1s ease-out forwards'
                    }}
                  >
                    ✨
                  </div>
                </div>
              ))}
              
              {/* Animated stress ball with new design */}
              <div 
                className={`relative mx-auto cursor-pointer select-none transition-all duration-300 ${
                  ballPressed ? 'scale-90' : 'scale-100 hover:scale-110'
                } ${stressLevel > 0 ? 'animate-bounce-slow' : ''}`}
                onMouseDown={() => handleStressBall(true)}
                onMouseUp={() => handleStressBall(false)}
                onMouseLeave={() => handleStressBall(false)}
                onTouchStart={() => handleStressBall(true)}
                onTouchEnd={() => handleStressBall(false)}
                style={{
                  animation: ballPressed ? 'none' : (stressLevel > 0 ? 'float 3s ease-in-out infinite' : 'none')
                }}
              >
                {/* Glowing background effect */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-300 via-teal-300 to-blue-300 blur-2xl opacity-50 animate-pulse"></div>
                
                {/* Main stress ball with gradient */}
                <div className="relative w-48 h-48 rounded-full bg-gradient-to-br from-emerald-400 via-teal-400 to-blue-400 flex items-center justify-center shadow-2xl border-4 border-white/30">
                  {/* Inner glow */}
                  <div className="absolute inset-4 rounded-full bg-gradient-to-br from-white/40 to-transparent"></div>
                  
                  {/* Emoji face */}
                  <div className="relative text-7xl z-10">
                    {stressBallEmoji}
                  </div>
                  
                  {/* Shine effect */}
                  <div className="absolute top-8 left-8 w-16 h-16 rounded-full bg-white/30 blur-xl"></div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold text-emerald-800">Digital Stress Ball</h3>
              <p className="text-emerald-600">Click and hold to squeeze the stress ball and watch its expression change as you reduce stress!</p>
              
              <div className="max-w-xs mx-auto">
                <div className="flex justify-between text-sm text-emerald-700 mb-2">
                  <span>Stress Level</span>
                  <span>{stressLevel}%</span>
                </div>
                <div className="w-full bg-emerald-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-emerald-500 to-red-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${stressLevel}%` }}
                  ></div>
                </div>
              </div>
              
              {stressLevel === 0 && (
                <div className="bg-emerald-100 border border-emerald-300 rounded-lg p-4 animate-bounce">
                  <p className="text-emerald-700 font-medium">🎉 Great job! You've reduced your stress level!</p>
                  <button
                    onClick={() => {
                      setStressLevel(100);
                      setPreviousStressLevel(100);
                    }}
                    className="mt-2 text-emerald-600 hover:text-emerald-700 underline"
                  >
                    Reset stress level
                  </button>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-emerald-800 mb-4">Antistress Relaxing Games</h1>
          <p className="text-xl text-emerald-600 max-w-2xl mx-auto">
            Take a break and engage with calming activities designed to reduce anxiety and promote mindfulness
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Game Selection Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-emerald-800 mb-4">Choose an Activity</h2>
              <div className="space-y-2">
                {games.map((game) => (
                  <button
                    key={game.id}
                    onClick={() => setActiveGame(game.id)}
                    className={`w-full text-left p-3 rounded-xl transition-all ${
                      activeGame === game.id
                        ? 'bg-emerald-100 text-emerald-800 border-2 border-emerald-300'
                        : 'hover:bg-emerald-50 text-emerald-700 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {game.icon}
                      <div>
                        <div className="font-medium">{game.name}</div>
                        <div className="text-xs opacity-75">{game.desc}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Game Area */}
          <div className="lg:col-span-3">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-8">
              {renderGame()}
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-emerald-800 mb-4">💡 Wellness Tips</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-emerald-700">
            <div className="flex items-start space-x-2">
              <Moon className="w-4 h-4 mt-0.5 text-emerald-500" />
              <p>Practice these activities for 5-10 minutes daily for best results</p>
            </div>
            <div className="flex items-start space-x-2">
              <Sun className="w-4 h-4 mt-0.5 text-emerald-500" />
              <p>Use breathing exercises during stressful moments throughout the day</p>
            </div>
            <div className="flex items-start space-x-2">
              <Heart className="w-4 h-4 mt-0.5 text-emerald-500" />
              <p>Combine these activities with regular exercise and healthy sleep for maximum benefit</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelaxGames;